import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Typography } from '@material-ui/core';
import type { FormHandles } from '@unform/core';
import { Form as UForm } from '@unform/web';
import Flex from 'components/Flex';
import {
  Button, ButtonIcon, DatePicker, Input, Select, WrapperLabel,
} from 'components/Forms';
import Layout from 'components/Layout';
import { pt } from 'date-fns/locale';
import { useHttp } from 'hooks/http';
import { useToast } from 'hooks/toast';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useHistory } from 'react-router-dom';
import {
  EventNoteIcon, InfoIcon, PlusIcon, TrashIcon,
} from 'styles/icons';
import { bairrosRJ, municipiosRJ, regioesRJ } from 'utils/regioesRJ';
import {
  Container, Content, GridButtons, WrapperBtnCancel, WrapperBtnInfo, WrapperBtnSubmit,
} from './styles';

registerLocale('pt-BR', pt);
interface IServiceRow {
  partner: string;
  service: string;
  quantity: string;
  services: { value: string; label: string }[];
}

interface ConfirmedService {
  partner_name: string;
  service_name: string;
  quantity: number | string;
}

const SocialAction: React.FC = () => {
  const history = useHistory();
  const labelRef = useRef<HTMLLabelElement>(null);
  const { httpGet, httpPost, loading } = useHttp();
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const [btnLoading, setBtnLoading] = useState(false);
  const [partners, setPartners] = useState<{ value: string; label: string }[]>([]);
  const [bairro, setBairro] = useState('');
  const [isBairroDisabled, setIsBairroDisabled] = useState(true);
  const [confirmedServices, setConfirmedServices] = useState<any[]>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [bairroOptions, setBairroOptions] = useState<{ value: string; label: string; }[]>([]);
  // eslint-disable-next-line max-len
  const [filteredMunicipios] = useState(municipiosRJ);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedRegiao, setSelectedRegiao] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedServices, setSelectedServices] = useState<{ [partnerId: string]: string[] }>({});
  // eslint-disable-next-line max-len
  const [selectedMunicipio, setSelectedMunicipio] = useState<string | null>(null);
  const [serviceRows, setServiceRows] = useState<IServiceRow[]>([{
    partner: '', service: '', quantity: '', services: [],
  }]);
  const [actionStatus] = useState([
    { value: true, label: 'Ativada' },
    { value: false, label: 'Desativada' },
  ]);


  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, max-len
  const handleServiceRowChange = (index: number, field: keyof IServiceRow, value: any) => {
    const updatedRows = [...serviceRows];
    updatedRows[index][field] = value;

    if (field === 'service') {
      const partnerId = updatedRows[index].partner;
      setSelectedServices((prev) => {
        const updatedSelectedServices = { ...prev };

        const previousService = serviceRows[index].service;
        if (previousService && updatedSelectedServices[partnerId]) {
          updatedSelectedServices[partnerId] = updatedSelectedServices[partnerId].filter(
            (id) => id !== previousService,
          );
        }

        updatedSelectedServices[partnerId] = [
          ...(updatedSelectedServices[partnerId] || []),
          value,
        ];

        return updatedSelectedServices;
      });
    }

    setServiceRows(updatedRows);
  };

  useEffect(() => {
    let isMounted = true;

    async function loadPartners(): Promise<void> {
      const { status, data } = await httpGet('social-action/partners/all');
      if (status === 200 && isMounted) {
        const stations = data.map((station: any) => ({
          value: station.id,
          label: `${station.partner_name}`,
        }));
        setPartners(stations);
      }
    }

    loadPartners();

    return () => {
      isMounted = false;
    };
  }, [httpGet]);

  const loadServicesByPartner = async (partnerId: string, index: number, isMounted = true): Promise<void> => {
    const { status, data } = await httpGet(`social-action/partners-services/${partnerId}/services`);

    if (!isMounted) return;

    if (status === 200) {
      const servicesData = data.map((service: any) => ({
        value: service.id,
        label: service.service_name,
      }));

      const alreadySelectedServices = serviceRows
        .filter((row, rowIndex) => rowIndex !== index)
        .map((row) => row.service);

      const filteredServices = servicesData.filter(
        (service: { value: string }) => !alreadySelectedServices.includes(service.value),
      );

      const updatedRows = [...serviceRows];
      updatedRows[index].services = filteredServices;

      if (isMounted) {
        setServiceRows(updatedRows);
      }
    }
  };


  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleMunicipioChange = (selectedOption: any) => {
    const municipioSelecionado = selectedOption?.value;
    setSelectedMunicipio(municipioSelecionado);

    const municipioData = municipiosRJ.find((m) => m.value === municipioSelecionado);

    if (municipioSelecionado === 'Rio de Janeiro') {
      setBairroOptions(bairrosRJ);
      setIsBairroDisabled(false);
      setBairro('');
      formRef.current?.setFieldValue('bairro', { value: '', label: '' });
    } else {
      setIsBairroDisabled(true);
      setBairro('Fora da Capital');
      formRef.current?.setFieldValue('bairro', {
        value: 'Fora da Capital',
        label: 'Fora da Capital'
      });
    }

    if (municipioData?.region) {
      formRef.current?.setFieldValue('regiao', {
        value: municipioData.region,
        label: municipioData.region,
      });
    }
  };

  const confirmService = () => {
    const lastRow = serviceRows[serviceRows.length - 1];

    if (lastRow.partner && lastRow.service && lastRow.quantity) {
      const alreadyExists = confirmedServices.some(
        (service) =>
          service.partner_id === lastRow.partner &&
          service.service_id === lastRow.service
      );

      if (alreadyExists) {
        addToast({
          type: 'error',
          title: 'Serviço já vinculado ao parceiro selecionado. Selecione um diferente.'
        });
        return;
      }

      const partnerName = partners.find((p) => p.value === lastRow.partner)?.label;
      const serviceName = lastRow.services.find((s) => s.value === lastRow.service)?.label;

      setConfirmedServices((prev) => [
        ...prev,
        {
          partner_id: lastRow.partner,
          service_id: lastRow.service,
          quantity: lastRow.quantity,
          partner_name: partnerName,
          service_name: serviceName,
        },
      ]);

      const updatedRows = [...serviceRows];
      updatedRows[updatedRows.length - 1] = {
        partner: '', service: '', quantity: '', services: [],
      };
      setServiceRows(updatedRows);
    }
  };

  const removeConfirmedService = (partnerId: string, serviceId: string) => {
    const updated = confirmedServices.filter(
      (service) => !(service.partner_id === partnerId && service.service_id === serviceId)
    );
    setConfirmedServices(updated);
  };

  const handleSubmit = useCallback(async (data: any) => {
    const errors: Record<string, string> = {};

  

    if (!data.data_acao) errors.data_acao = 'Informe a data da ação';
    if (!data.municipio?.value) errors.municipio = 'Informe o município';

    if (Object.keys(errors).length > 0) {
      formRef.current?.setErrors(errors);
      addToast({
        type: 'error',
        title: 'Preencha todos os campos obrigatórios',
      });
      return;
    }

    const formattedDataAcao = new Date(data.data_acao).toISOString().split('T')[0];
    if (confirmedServices.length === 0) {
      addToast({
        type: 'error',
        title: 'É necessário adicionar ao menos um serviço antes de enviar.',
      });
      return;
    }

    const socialActionPayload = {
      data_acao: formattedDataAcao,
      regiao: data.regiao.value,
      municipio: data.municipio.value,
      attendance_count: Number(data.attendance_count),
      action_status: data.action_status?.value ?? true,
      bairro: data.bairro?.value || 'Fora da Capital',
      services: confirmedServices,
    };

    setFormData(socialActionPayload);
    setIsConfirmDialogOpen(true);
  }, [confirmedServices, addToast]);

  const submitConfirmedData = async () => {
    try {
      setBtnLoading(true);

      const { services, ...socialActionPayload } = formData;

      const socialActionResponse = await httpPost('social-action', socialActionPayload);

      if (socialActionResponse.status === 201) {
        const socialActionId = socialActionResponse.data.id;

        const serviceDetails = services.map((service: any) => ({
          social_action_id: socialActionId,
          partner_id: service.partner_id,
          service_id: service.service_id,
          quantity: service.quantity,
        }));

        const serviceResponse = await httpPost('social-action/details', { services: serviceDetails });

        if (serviceResponse.status === 201) {
          addToast({ title: 'Ação social e detalhes salvos com sucesso!', type: 'success' });
          history.push('/social-action/list');
        } else {
          addToast({ title: 'Erro ao salvar os detalhes!', type: 'error' });
        }
      } else {
        addToast({ title: 'Erro ao salvar a ação social!', type: 'error' });
      }

      setBtnLoading(false);
      setIsConfirmDialogOpen(false);
    } catch (error) {
      setBtnLoading(false);
      addToast({ title: 'Erro ao salvar a ação social!', type: 'error' });
    }
  };

  const breadcrumbs = {
    icon: EventNoteIcon,
    links: [{ path: '', title: 'Ação Social' }],
  };

  return (
    <Layout breadcrumbs={breadcrumbs} loading={loading}>
      <Container>
        <UForm ref={formRef} onSubmit={handleSubmit} initialData={{ action_status: { value: true, label: 'Ativo' } }}>
          <Content>
            <Grid container spacing={1}>
              <WrapperLabel label="Data da Ação" xs={12} md={6} sm={6}>
                <DatePicker name="data_acao" />
              </WrapperLabel>
              <WrapperLabel label="Município" xs={12} md={6} sm={6}>
                <Select
                  name="municipio"
                  options={filteredMunicipios}
                  // eslint-disable-next-line max-len
                  value={selectedMunicipio ? { value: selectedMunicipio, label: selectedMunicipio } : null}
                  onChange={handleMunicipioChange}

                />
              </WrapperLabel>
              <WrapperLabel label="Região" xs={12} md={6} sm={6}>
                <Select
                  name="regiao"
                  options={regioesRJ}
                  isDisabled
                />
              </WrapperLabel>
              <WrapperLabel label="Bairro" xs={12} md={6} sm={6}>
                <Select
                  name="bairro"
                  options={bairroOptions}
                  value={bairro ? { value: bairro, label: bairro } : null}
                  onChange={(e: any) => setBairro(e.value)}
                  // eslint-disable-next-line max-len
                  isDisabled={isBairroDisabled || !selectedMunicipio}
                />
              </WrapperLabel>
              <WrapperLabel label="Público presente" xs={12} md={6} sm={6}>
                <Input
                  name='attendance_count'
                />
              </WrapperLabel>
              <WrapperLabel label="Status" xs={12} md={6} sm={6}>
                <Select
                  name='action_status'
                  options={actionStatus}
                />
              </WrapperLabel>

              {serviceRows.map((row, index) => (
                <React.Fragment key={index}>
                  <WrapperLabel label="Parceiro" xs={12} md={6} sm={6} lg={4}>
                    <Select
                      name={`services[${index}].partner_id`}
                      value={
                        row.partner
                          ? { value: row.partner, label: partners.find((p) => p.value === row.partner)?.label }
                          : null
                      }
                      options={partners}
                      onChange={(selectedOption: any) => {
                        handleServiceRowChange(index, 'partner', selectedOption.value);
                        loadServicesByPartner(selectedOption.value, index);
                      }}
                    />
                  </WrapperLabel>
                  <WrapperLabel label="Serviço" xs={12} md={6} sm={6} lg={4}>
                    <Select
                      name={`services[${index}].service_id`}
                      value={
                        row.service
                          ? { value: row.service, label: row.services.find((s) => s.value === row.service)?.label }
                          : null
                      }
                      options={row.services}
                      onChange={(selectedOption: any) => handleServiceRowChange(index, 'service', selectedOption.value)}
                    />
                  </WrapperLabel>
                  <WrapperLabel label="Quantidade" xs={12} md={12} sm={12} lg={4}>
                    <Input
                      name={`services[${index}].quantity`}
                      value={row.quantity}
                      onChange={(e: any) => handleServiceRowChange(index, 'quantity', e.target.value)}
                    />
                    <ButtonIcon title="Adicionar Serviço" onClick={confirmService}>
                      <PlusIcon />
                    </ButtonIcon>
                  </WrapperLabel>
                </React.Fragment>
              ))}

              {confirmedServices.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 20,
                    marginTop: 20,
                    backgroundColor: '#FFF7ED',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 12, color: '#F97316' }}
                  >
                    Relatório Geral
                  </Typography>

                  {Object.entries(
                    confirmedServices.reduce((acc: Record<string, number>, item) => {
                      const partnerName = item.partner_name || 'Parceiro não identificado';
                      const quantity = Number(item.quantity) || 0;
                      acc[partnerName] = (acc[partnerName] || 0) + quantity;
                      return acc;
                    }, {})
                  )
                    .reverse()
                    .map(([partnerName, total]) => (
                      <div
                        key={partnerName}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 0',
                          borderBottom: '1px solid #FFE4C7',
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 500,
                            flex: 1,
                            marginRight: 30,
                            wordBreak: 'break-word',
                          }}
                        >
                          {partnerName}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 700, minWidth: 30, textAlign: 'right' }}
                        >
                          {total}
                        </Typography>
                      </div>
                    ))}
                </div>
              )}

              {(() => {
                const grouped = confirmedServices.reduce<Record<string, any[]>>((acc, service) => {
                  const partnerName = service.partner_name || 'Parceiro não identificado';
                  if (!acc[partnerName]) {
                    acc[partnerName] = [];
                  }
                  acc[partnerName].push(service);
                  return acc;
                }, {});
                const entries = Object.entries(grouped).reverse();
                return entries.map(([partnerName, services], index) => (
                  <Container key={partnerName} title="Parceiros presentes e serviços disponibilizados: ">
                    <Grid container spacing={1} alignItems='center'>
                      <p style={{ fontWeight: "bold" }}>{entries.length - index} - {partnerName}</p>
                      {services.map((item, index) => (
                        <Grid container key={index} spacing={1} justify="space-between" alignItems='center' style={{ marginTop: 8 }}>
                          <Flex title="Serviço" xs={12} sm={7} md={7}>
                            {item.service_name}
                          </Flex>
                          <Flex title="Quantidade" xs={12} sm={3} md={3}>
                            {item.quantity}
                          </Flex>
                          <Flex title='' xs={12} sm={2} md={2}>
                            <TrashIcon
                              onClick={() => removeConfirmedService(item.partner_id, item.service_id)}
                              color="red"
                              cursor="pointer"
                              title="Remover"
                            />
                          </Flex>
                        </Grid>
                      ))}
                    </Grid>
                  </Container>
                ));
              })()}

              <GridButtons>
                <WrapperBtnInfo>
                  <ButtonIcon
                    title="Instruções"
                    onClick={() => labelRef.current?.click()}
                  >
                    <InfoIcon />
                  </ButtonIcon>
                </WrapperBtnInfo>
                <WrapperBtnCancel>
                  <Button
                    type="button"
                    onClick={() => { history.push('/social-action/list'); }}
                  >
                    Cancelar
                  </Button>
                </WrapperBtnCancel>
                <WrapperBtnSubmit>
                  <Button
                    type="submit"
                    isLoading={btnLoading}
                  >
                    Enviar
                  </Button>
                </WrapperBtnSubmit>
              </GridButtons>
            </Grid>
          </Content>
        </UForm>
      </Container>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography style={{ fontWeight: 'bold', color: '#F97316' }}>
            Confirmação da Ação Social
          </Typography>
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: '#FFF7ED' }}>
          <Grid container spacing={3} style={{ paddingBottom: 20 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Data da Ação</Typography>
              <Typography variant="body1">{formData?.data_acao}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Município</Typography>
              <Typography variant="body1">
                {formData?.municipio}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Região</Typography>
              <Typography variant="body1">
                {formData?.regiao}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Bairro</Typography>
              <Typography variant="body1">
                {formData?.bairro}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Público Presente</Typography>
              <Typography variant="body1">
                {formData?.attendance_count || "Não cadastrado"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Status</Typography>
              <Typography variant="body1">
                {formData?.action_status ? 'Ativado' : 'Desativado'}
              </Typography>
            </Grid>

          </Grid>


          <Divider style={{ margin: '20px 0' }} />

          <Grid container spacing={3} style={{ paddingBottom: 20 }}>
            <Grid item xs={6}>
              <div style={{
                backgroundColor: '#FFEDD5',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#EA580C', marginBottom: 2 }}>
                  {formData?.services ? new Set(formData.services.map((s: any) => s.partner_name)).size : 0}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Parceiros
                </Typography>
              </div>
            </Grid>

            <Grid item xs={6}>
              <div style={{
                backgroundColor: '#FFEDD5',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#EA580C', marginBottom: 2 }}>
                  {formData?.services?.reduce((acc: number, cur: any) => acc + Number(cur.quantity), 0)}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Serviços
                </Typography>
              </div>
            </Grid>
          </Grid>

          <Divider style={{ marginBottom: 20 }} />

          {(() => {
            const grouped = (formData?.services as ConfirmedService[] || []).reduce<Record<string, ConfirmedService[]>>(
              (acc, service) => {
                const partnerName = service.partner_name || 'Parceiro não identificado';
                if (!acc[partnerName]) {
                  acc[partnerName] = [];
                }
                acc[partnerName].push(service);
                return acc;
              },
              {}
            );

            const entries = Object.entries(grouped).reverse();

            return entries.map(([partnerName, services], index) => (
              <div key={partnerName} style={{ marginBottom: 20 }}>
                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                  <Grid item>
                    <Typography style={{
                      backgroundColor: '#F97316',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 30,
                      height: 30,
                      textAlign: 'center',
                      lineHeight: '30px',
                      fontWeight: 'bold',
                    }}>
                      {entries.length - index}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {partnerName} — {services.reduce((acc, s) => acc + Number(s.quantity || 0), 0)} atendimentos
                    </Typography>
                  </Grid>
                </Grid>

                {services.map((service, serviceIndex) => (
                  <Grid
                    container
                    key={serviceIndex}
                    spacing={2}
                    style={{
                      marginBottom: serviceIndex === services.length - 1 ? 0 : 12,
                      backgroundColor: '#FFEDD5',
                      borderRadius: 8,
                      padding: 10,
                      paddingLeft: 45,
                    }}
                  >
                    <Grid item xs={9}>
                      <Typography style={{ color: '#EA580C', fontWeight: 600 }}>
                        {service.service_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography style={{
                        color: '#EA580C',
                        borderRadius: '50%',
                        width: 30,
                        height: 30,
                        textAlign: 'center',
                        lineHeight: '30px',
                        fontWeight: 'bold',
                        float: 'right',
                        marginTop: '4px',
                      }}>
                        {service.quantity}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </div>
            ));
          })()}

        </DialogContent>

        <DialogActions style={{ padding: 16 }}>
          <Button onClick={() => setIsConfirmDialogOpen(false)}>
            Voltar para Edição
          </Button>
          <Button onClick={submitConfirmedData} isLoading={btnLoading} style={{ backgroundColor: '#F97316', color: '#fff' }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default SocialAction;
